/**
 * One set of spending categories, whichever bank a transaction came from.
 *
 * Every bank labels spending its own way — Amex "Travel-Airline", Apple
 * "Airlines", Capital One "Airfare", Discover "Travel/ Entertainment", Chase
 * "Travel" — and those labels used to be stored and shown as they came. So the
 * same kind of spending split across categories by which card paid for it:
 * Categories showed "Travel" with Air France and Lufthansa in it and, beside
 * it, an "Airline" category holding Aer Lingus.
 *
 * `canonicalCategory` maps a bank's label onto `FINANCE_CATEGORIES`. It runs
 * when a transaction is READ (`effectiveCategory`), not only at import, so
 * transactions stored before it existed are shown in the right category too —
 * in the app and in the assistant's answers alike. A label it doesn't know is
 * "Other", never a new top-level category; the bank's own words stay on the
 * transaction (`originalCategory`) for the detail view.
 */
/** The categories spending is grouped into, in display order of importance. */
export const FINANCE_CATEGORIES = [
    'Housing',
    'Groceries',
    'Food & Dining',
    'Transportation',
    'Travel',
    'Shopping',
    'Bills & Utilities',
    'Subscriptions',
    'Healthcare',
    'Entertainment',
    'Personal',
    'Education',
    'Gifts & Donations',
    'Fees & Interest',
    'Taxes',
    'Cash',
    'Other',
];
/** Not spending at all — what the importer files money movements and deposits under. */
const NON_SPENDING = new Set(['Income', 'Transfer']);
/**
 * Bank labels → category, by keyword. Checked in order, first match wins, so
 * the more specific words come first ("gas station" before "station",
 * "airline" before "line").
 */
const KEYWORDS = [
    // Travel: flights, stays, rentals, travel agencies.
    [/\bair(line|lines|fare|travel)?\b|\bflight|\bhotel|\blodging|\bmotel|\bresort|car rental|vehicle rental|rental car|\bcruise|\btravel\b|\btimeshare/i, 'Travel'],
    // Getting around day to day.
    [/\bgas\b|gasoline|\bfuel|gas station|service station|\bauto(motive)?\b|\bparking|\btoll|\btaxi|rideshare|\btransit|\btransport|\bev charging/i, 'Transportation'],
    [/grocer|supermarket|\bfood store/i, 'Groceries'],
    [/restaurant|dining|food & drink|\bfood\b|fast food|coffee|\bcaf[eé]|\bbars?\b|alcohol|\bdrink/i, 'Food & Dining'],
    [/\brent\b|mortgage|\bhousing|home owners|\bhoa\b/i, 'Housing'],
    [/utilit|\bcable|internet|\bphone|mobile telecom|telecom|wireless|communication|\belectric|\bwater\b|\bbills\b|insurance/i, 'Bills & Utilities'],
    [/subscription|streaming|membership/i, 'Subscriptions'],
    [/medical|health|pharmac|\bdrug|doctor|dental|vision|hospital|wellness/i, 'Healthcare'],
    [/entertain|movie|music|\bevent|recreation|amusement|\bsports?\b|\bgames?\b/i, 'Entertainment'],
    [/education|tuition|school|\bbooks? ?stores?/i, 'Education'],
    [/\bgift|donation|charit/i, 'Gifts & Donations'],
    [/\bfees?\b|interest charge|finance charge|adjustment|late charge/i, 'Fees & Interest'],
    [/\btax|government/i, 'Taxes'],
    [/\batm\b|\bcash\b/i, 'Cash'],
    [/personal|beauty|salon|\bspa\b|\bpets?\b|\bkids|child|fitness|\bgym/i, 'Personal'],
    [/merchandise|shopping|department|clothing|apparel|electronics|computer|home improvement|\bhome\b|furnish|hardware|wholesale|warehouse|general|online|retail|sporting|office supplies|\bstores?\b/i, 'Shopping'],
];
const CANONICAL_BY_LOWER = new Map([
    ...[...FINANCE_CATEGORIES, ...NON_SPENDING].map((c) => [c.toLowerCase(), c]),
    // Discover's "Services" is mostly phone, internet and insurance.
    ['services', 'Bills & Utilities'],
]);
/**
 * The app's category for a bank's label.
 *
 * An Amex label ("Travel-Airline", "Merchandise & Supplies-Groceries") is
 * read by its specific part first, then its group. Empty or unknown → "Other"
 * (empty → "Uncategorized" is kept apart only when there is no label at all,
 * so a never-categorised row still says so).
 */
export function canonicalCategory(raw) {
    const label = (raw ?? '').trim();
    if (!label)
        return 'Uncategorized';
    const exact = CANONICAL_BY_LOWER.get(label.toLowerCase());
    if (exact)
        return exact;
    if (label.toLowerCase() === 'uncategorized')
        return 'Uncategorized';
    // Amex: "Group-Specific". The specific part says more ("Travel-Airline",
    // "Merchandise & Supplies-Groceries", "Business Services-Insurance").
    const dash = label.indexOf('-');
    const parts = dash > 0 ? [label.slice(dash + 1), label.slice(0, dash)] : [label];
    for (const part of parts) {
        const p = part.trim();
        const hit = CANONICAL_BY_LOWER.get(p.toLowerCase());
        if (hit)
            return hit;
        for (const [re, category] of KEYWORDS)
            if (re.test(p))
                return category;
    }
    return 'Other';
}
// ─── Kinds within a category ────────────────────────────────────────────────
/**
 * What kind of spending a transaction is within its category — Flights,
 * Lodging, Fuel, Coffee — from its merchant, its description and the bank's
 * label.
 *
 * The Categories view used the bank's label as this level, so Travel held a
 * "Travel" group (Chase's label) beside an "Airline" group (Amex's) with
 * airlines in both. These kinds are the app's, the same whichever card paid.
 * A category without kinds answers null: its merchants are listed directly.
 */
const KINDS = {
    Travel: [
        [/airline|airways|airfare|\bair ?lines?\b|lufthansa|aer lingus|air france|air canada|air india|klm\b|british a|delta air|united air|american air|alaska air|\balaska a\b|southwest|jetblue|spirit air|frontier|allegiant|allegnt|hawaiian|singapore ?air|emirates|qatar|etihad|turkish air|ryanair|easyjet|wizz|virgin atl|iberia|swiss int|austrian|cathay|ana\b|jal\b|aeromexico|copa air|westjet|porter air|sun country/i, 'Flights'],
        [/hotel|airbnb|marriott|hilton|hyatt|sheraton|westin|holiday inn|\binn\b|motel|lodg|vrbo|hostel|resort|ihg|wyndham|best western|radisson|four seasons/i, 'Lodging'],
        [/car rental|rent-?a-?car|hertz|enterprise|alamo|avis\b|budget rent|national car|sixt|turo|thrifty|dollar rent|vehicle rental/i, 'Car rental'],
        [/\buber\b(?! *eats)|\blyft|taxi|\bcab\b/i, 'Rideshare & taxi'],
        [/amtrak|greyhound|\brail|train|flixbus|megabus|eurostar|\bbus\b/i, 'Trains & buses'],
        [/expedia|booking\.com|priceline|travelbrands|intair|kayak|hopper|orbitz|travelocity|agoda|trip\.com|travel agenc/i, 'Travel booking'],
    ],
    Transportation: [
        [/\bgas\b|fuel|gasoline|shell|chevron|exxon|mobil|arco|76 |texaco|valero|costco gas|sunoco|bp\b|speedway|circle k|wawa|quiktrip/i, 'Fuel'],
        [/parking|park mobile|parkmobile|toll|fastrak|e-?zpass|spothero/i, 'Parking & tolls'],
        [/\buber\b(?! *eats)|\blyft|taxi|\bcab\b/i, 'Rideshare & taxi'],
        [/transit|metro|\bbart\b|caltrain|clipper|\bmta\b|subway fare|bus fare|\btrimet/i, 'Public transit'],
        [/ev charg|chargepoint|electrify|supercharg|tesla charg|evgo/i, 'EV charging'],
        [/auto|tire|jiffy|oil change|repair|car wash|dmv|registration/i, 'Car care & fees'],
    ],
    'Food & Dining': [
        [/doordash|uber ?eats|grubhub|postmates|caviar|seamless|deliveroo/i, 'Delivery'],
        [/starbucks|coffee|cafe|caf[eé]|peet|dutch bros|tim hortons|blue bottle|philz|dunkin/i, 'Coffee'],
        [/mcdonald|taco bell|chipotle|subway\b|burger|wendy|kfc|popeye|chick-?fil|in-n-out|five guys|domino|pizza hut|panda express|jack in the box|sonic drive/i, 'Fast food'],
        [/\bbar\b|\bpub\b|brew|tavern|taproom|winery|liquor|wine\b|beer/i, 'Bars & drinks'],
    ],
    'Bills & Utilities': [
        [/verizon|t-?mobile|at&t|sprint|mint mobile|visible|cricket|comcast|xfinity|spectrum|cox comm|frontier comm|centurylink|google fi|\bphone|wireless|internet|cable|telecom/i, 'Phone & internet'],
        [/electric|power|pg&e|edison|energy|duke|pacific power|\bgas co|natural gas|utility/i, 'Electricity & gas'],
        [/water|sewer|trash|waste|garbage|recology/i, 'Water & trash'],
        [/insurance|geico|state farm|allstate|progressive|lemonade|usaa|farmers ins|liberty mutual/i, 'Insurance'],
    ],
    Shopping: [
        [/amazon|amzn|ebay|etsy|temu|shein|aliexpress|wayfair/i, 'Online'],
        [/costco|sam'?s club|bj'?s wholesale|warehouse|wholesale/i, 'Warehouse clubs'],
        [/home depot|lowe'?s|ikea|ace hardware|menards|bed bath|crate|pottery barn|furnish|hardware|home improvement/i, 'Home & hardware'],
        [/best buy|apple store|b&h|micro center|newegg|electronics|computer/i, 'Electronics'],
        [/target|walmart|macy|kohl|nordstrom|jcpenney|ross|marshalls|tj ?maxx|department/i, 'Department stores'],
        [/uniqlo|nike|adidas|gap\b|old navy|zara|h&m|lululemon|clothing|apparel|shoe/i, 'Clothing'],
    ],
};
/** The kind within the category, or null when the category isn't split. */
export function spendingKind(category, t) {
    const kinds = KINDS[category];
    if (!kinds)
        return null;
    const text = [t.merchant, t.description, t.subCategory, t.originalCategory].filter(Boolean).join(' ');
    for (const [re, kind] of kinds)
        if (re.test(text))
            return kind;
    return `Other ${category.toLowerCase()}`;
}
//# sourceMappingURL=finance-categories.js.map